import React, { useEffect, useRef, useState } from "react";
import "./AnimatedMenu.css";

export interface IAnimatedMenuController{
    itemsContainerRef: React.RefObject<HTMLDivElement>,
    menuRef: React.RefObject<HTMLDivElement>,
    selectedRect: DOMRect,
    selectedIndex: number,
    setSelectedIndex: React.Dispatch<React.SetStateAction<number>>,
    items: Array<string | React.ReactNode>
}

export interface IAnimatedMenuProps {
    items: Array<string | React.ReactNode>,
    selectedIndex: number,
    onSelect: (index: number)=>void,
    itemsRef?: React.RefObject<HTMLDivElement>
}

interface IAnimatedMenuControllerProps {
    View: React.FC<{controller: IAnimatedMenuController}>,
    childrenSelector?: (parent:HTMLElement)=>HTMLElement[]
}

export const AnimatedMenuController = ({items, selectedIndex, onSelect, itemsRef, View, childrenSelector}: IAnimatedMenuProps & IAnimatedMenuControllerProps)=>{
    const _itemsContainerRef = useRef<HTMLDivElement>(null);
    const itemsContainerRef = itemsRef || _itemsContainerRef;
    const menuRef = useRef<HTMLDivElement>(null);
    const setSelectedIndex = onSelect;
    const [selectedRect, setSelectedRect] = useState<DOMRect>(null);
    const [sizes, setSizes] = useState<Array<DOMRect>>([]);
    const [resizeFix, setResizeFix] = useState(0);
    const [mutationFix, setMutationFix] = useState(0);

    useEffect(()=>{
        console.log(itemsContainerRef.current);
        if (!itemsContainerRef.current){
            return;
        }
        const config: MutationObserverInit = { attributes: true, childList: true, subtree: true, characterData: true};
        const callback: MutationCallback = (mutationList, _observer) => {
            setMutationFix(last=>last+1);
        }         
        const observer = new MutationObserver(callback);
        observer.observe(itemsContainerRef.current, config);
        return ()=>{
            observer.disconnect(); 
        }
    }, [itemsContainerRef.current]);


    useEffect(()=>{
        if (!itemsContainerRef.current){
            return;
        }
        const children = childrenSelector ? childrenSelector(itemsContainerRef.current) : [...itemsContainerRef.current.children];
        const _sizes = children.map(child=>{
            const el = child as HTMLDivElement;
            const rect = new DOMRect(
                (-itemsContainerRef.current.getBoundingClientRect().left + el.getBoundingClientRect().left) / itemsContainerRef.current.clientWidth * 100, 
                (-itemsContainerRef.current.getBoundingClientRect().top + el.getBoundingClientRect().top) / itemsContainerRef.current.clientHeight * 100, 
                el.offsetWidth, 
                el.offsetHeight
            )
            return rect;
        });
        setSizes(_sizes);
    }, [itemsContainerRef.current, items, resizeFix, mutationFix]);

    useEffect(()=>{
        const handler = ()=>{
            setResizeFix(window.innerWidth);
        };
        window.addEventListener('resize', handler);
        return ()=> window.removeEventListener('resize', handler);
    }, []);

    useEffect(()=>{
        const current = sizes?.[selectedIndex];
        if (!current){
            if (!sizes?.[0]){
                return;
            }
            setSelectedRect(new DOMRect(
                -10,
                sizes?.[0]?.top,
                0,
                sizes?.[0]?.height
            ));
            return;
        }
        setSelectedRect(new DOMRect(
            current.left,
            current.top,
            current.width,
            current.height
        ));
    }, [sizes, selectedIndex]);

    return <View controller={{
        itemsContainerRef,
        menuRef,
        selectedIndex,
        setSelectedIndex,
        selectedRect,
        items
    }} />
}

export interface IAnimatedMenuItemProps{
    itemContent: string | React.ReactNode,
    onClick: ()=>void
}

const AnimatedMenuItem = ({itemContent, onClick}: IAnimatedMenuItemProps)=>{
    return <div className="button AnimatedMenuItem" onClick={onClick}>
        {itemContent}
    </div>
}

export const AnimatedMenuViewGeneric = ({controller, ItemView}:{controller: IAnimatedMenuController, ItemView: React.FC<IAnimatedMenuItemProps>})=>{
    const {selectedRect, menuRef, itemsContainerRef, selectedIndex, setSelectedIndex, items} = controller;

    return <div ref={menuRef} className="AnimatedMenu">
        {selectedRect &&
            <div className={`AnimatedMenuItem_select ${selectedIndex < 0 ? 'AnimatedMenuItem_select--hide' : ''}`} style={{
                left: `${selectedRect.left}%`,
                top: `${selectedRect.top}%`,
            }}>
                <div className="AnimatedMenuItem_select_sizer" style={{
                    width: `${selectedRect.width}px`,
                    height: `${selectedRect.height}px`,
                }}>
            </div>
        </div>}
        <div ref={itemsContainerRef} className="AnimatedMenuItem_items">
            {items.map((it, i)=>{
                return <ItemView key={i} itemContent={it} onClick={()=>setSelectedIndex(i)}></ItemView>
            })}
        </div>
    </div>
}

const AnimatedMenuView = ({controller}: {controller: IAnimatedMenuController})=>{
    return <AnimatedMenuViewGeneric 
        controller={controller} 
        ItemView={AnimatedMenuItem}>
    </AnimatedMenuViewGeneric>
}

export const AnimatedMenu = ({items, selectedIndex, onSelect, itemsRef}: IAnimatedMenuProps)=>{
    return <AnimatedMenuController
        items={items}
        selectedIndex={selectedIndex}
        onSelect={onSelect}
        itemsRef={itemsRef}
        View={AnimatedMenuView}
    ></AnimatedMenuController>
}